import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
  Spinner,
  Badge,
} from "reactstrap";
import {
  MapPin,
  Building,
  Phone,
  Mail,
  Globe,
  Star,
  Info,
  Users,
  FileText,
  Shield,
  Award,
  Clock,
  CheckCircle,
} from "lucide-react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Contractor } from "../../models/Contractor.interface";
import ArrayInput from "../../Components/FormFields/ArrayInput";
import ImageUpload from "../../Components/FormFields/ImageUpload";
import ObjectInput from "../../Components/FormFields/ObjectInput";
import TimeInput from "../../Components/FormFields/TimeInput";
import { Address } from "../../models/Address.interface";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import classnames from "classnames";

const ContractorFormPage: React.FC = () => {
  const { contractorId } = useParams<{ contractorId: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(contractorId);

  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState<Contractor>({
    businessName: "",
    contractorId: "",
    about: "",
    email: "",
    phone: "",
    phone2: "",
    website: "",
    slogan: "",
    description: "",
    address: {
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
    },
    categories: [],
    services: [],
    servicesProducts: [],
    brands: [],
    rating: 0,
    reviewCount: "0",
    accreditation: [],
    searches: [],
    specialOffer: "",
    socialLinks: {},
    hours: {},
    licenses: [],
    insurance: {},
    bond: {},
    workersComp: {},
    employees: [],
  });

  useEffect(() => {
    const fetchContractor = async () => {
      if (!isEditMode) return;

      setLoading(true);
      setError(null);

      try {
        const firestore = firebase.firestore();
        const contractorDoc = await firestore
          .collection("contractors")
          .where("contractorId", "==", contractorId)
          .limit(1)
          .get();

        if (contractorDoc.empty) {
          setError("Contractor not found");
          return;
        }

        const contractorData = {
          id: contractorDoc.docs[0].id,
          ...contractorDoc.docs[0].data(),
        } as Contractor;

        setFormData(contractorData);
      } catch (err) {
        console.error("Error fetching contractor:", err);
        setError("Failed to load contractor data");
      } finally {
        setLoading(false);
      }
    };

    fetchContractor();
  }, [contractorId, isEditMode]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    section: string = "basic"
  ) => {
    const { name, value } = e.target;

    if (section === "address") {
      setFormData(
        (prev) =>
          ({
            ...prev,
            address: {
              ...(prev.address || {}),
              [name]: value,
            },
          } as Contractor)
      );
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const firestore = firebase.firestore();

      if (!formData.businessName) {
        throw new Error("Business name is required");
      }

      if (!isEditMode) {
        formData.contractorId = `CTR${Date.now()}`;
      }

      const contractorData = {
        ...formData,
      };

      if (isEditMode) {
        await firestore
          .collection("contractors")
          .doc(formData.id)
          .update(contractorData);

        setSuccess("Contractor updated successfully");
      } else {
        await firestore.collection("contractors").add(contractorData);
        setSuccess("Contractor added successfully");
      }

      setTimeout(() => {
        navigate("/contractors");
      }, 2000);
    } catch (err) {
      console.error("Error saving contractor:", err);
      setError(
        err instanceof Error ? err.message : "Failed to save contractor"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center p-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading contractor data...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEditMode ? "Edit Contractor" : "Add Contractor"}
          pageTitle="Contractors"
        />

        <Row>
          <Col>
            <Button
              color="secondary"
              className="mb-4"
              onClick={() => navigate("/contractors")}
            >
              ← Back to Contractors List
            </Button>
          </Col>
        </Row>

        {error && (
          <Alert color="danger" className="mb-4">
            <Info className="me-2" size={18} />
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" className="mb-4">
            <CheckCircle className="me-2" size={18} />
            {success}
          </Alert>
        )}

        <Card className="shadow-sm">
          <CardBody className="p-0">
            <Nav tabs className="nav-tabs-custom border-bottom-0">
              <NavItem>
                <NavLink
                  className={classnames("px-4 py-3 border-0 cursor-pointer", {
                    active: activeTab === "basic",
                  })}
                  onClick={() => setActiveTab("basic")}
                >
                  <Building className="me-2" size={18} />
                  Basic Information
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames("px-4 py-3 border-0 cursor-pointer", {
                    active: activeTab === "contact",
                  })}
                  onClick={() => setActiveTab("contact")}
                >
                  <Phone className="me-2" size={18} />
                  Contact Details
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames("px-4 py-3 border-0 cursor-pointer", {
                    active: activeTab === "services",
                  })}
                  onClick={() => setActiveTab("services")}
                >
                  <Star className="me-2" size={18} />
                  Services & Categories
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames("px-4 py-3 border-0 cursor-pointer", {
                    active: activeTab === "credentials",
                  })}
                  onClick={() => setActiveTab("credentials")}
                >
                  <Shield className="me-2" size={18} />
                  Credentials
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames("px-4 py-3 border-0 cursor-pointer", {
                    active: activeTab === "team",
                  })}
                  onClick={() => setActiveTab("team")}
                >
                  <Users className="me-2" size={18} />
                  Team & Hours
                </NavLink>
              </NavItem>
            </Nav>
          </CardBody>
        </Card>

        <Card className="shadow-sm">
          <CardBody className="p-4">
            <Form onSubmit={handleSubmit}>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="basic">
                  <h4 className="mb-4">Company Information</h4>
                  <Row className="g-4">
                    <Col md={6}>
                      <FormGroup>
                        <Label for="businessName" className="fw-bold">
                          Business Name *
                        </Label>
                        <Input
                          id="businessName"
                          name="businessName"
                          value={formData.businessName}
                          onChange={handleInputChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="slogan" className="fw-bold">
                          Business Slogan
                        </Label>
                        <Input
                          id="slogan"
                          name="slogan"
                          value={formData.slogan || ""}
                          onChange={handleInputChange}
                          placeholder="Enter a catchy slogan"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col md={12}>
                      <ImageUpload
                        label="Business Logo"
                        currentImage={formData.logo}
                        onUpload={(url) =>
                          setFormData((prev) => ({ ...prev, logo: url }))
                        }
                        folder="contractor-logos"
                      />
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col md={12}>
                      <FormGroup>
                        <Label for="about" className="fw-bold">
                          About the Business
                        </Label>
                        <Input
                          type="textarea"
                          id="about"
                          name="about"
                          value={formData.about || ""}
                          onChange={handleInputChange}
                          rows={4}
                          placeholder="Provide a brief overview of your business"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col md={12}>
                      <FormGroup>
                        <Label for="description" className="fw-bold">
                          Detailed Description
                        </Label>
                        <Input
                          type="textarea"
                          id="description"
                          name="description"
                          value={formData.description || ""}
                          onChange={handleInputChange}
                          rows={6}
                          placeholder="Describe your services, expertise, and what makes your business unique"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col md={12}>
                      <FormGroup>
                        <Label for="specialOffer" className="fw-bold">
                          Special Offer
                        </Label>
                        <Input
                          type="textarea"
                          id="specialOffer"
                          name="specialOffer"
                          value={formData.specialOffer || ""}
                          onChange={handleInputChange}
                          rows={3}
                          placeholder="Enter any special offers or promotions"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="contact">
                  <h4 className="mb-4 d-flex align-items-center">
                    Contact Information
                  </h4>
                  <Row className="g-4">
                    <Col md={6}>
                      <FormGroup>
                        <Label for="phone" className="fw-bold">
                          Primary Phone
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone || ""}
                          onChange={handleInputChange}
                          placeholder="(555) 555-5555"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="phone2" className="fw-bold">
                          Secondary Phone
                        </Label>
                        <Input
                          id="phone2"
                          name="phone2"
                          value={formData.phone2 || ""}
                          onChange={handleInputChange}
                          placeholder="(555) 555-5555"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="g-4">
                    <Col md={6}>
                      <FormGroup>
                        <Label for="email" className="fw-bold">
                          Email Address
                        </Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email || ""}
                          onChange={handleInputChange}
                          placeholder="business@example.com"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="website" className="fw-bold">
                          Website
                        </Label>
                        <Input
                          id="website"
                          name="website"
                          value={formData.website || ""}
                          onChange={handleInputChange}
                          placeholder="https://www.example.com"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="mt-4">
                    <h4 className="mb-2 d-flex align-items-center">
                      Business Address
                    </h4>
                    <ObjectInput
                      label=""
                      value={formData.address || {}}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          address: value as Address,
                        }))
                      }
                      fields={[
                        {
                          key: "address1",
                          label: "Address Line 1",
                          type: "text",
                        },
                        {
                          key: "address2",
                          label: "Address Line 2",
                          type: "text",
                        },
                        {
                          key: "city",
                          label: "City",
                          type: "text",
                        },
                        {
                          key: "state",
                          label: "State",
                          type: "text",
                        },
                        {
                          key: "zipCode",
                          label: "ZIP Code",
                          type: "text",
                        },
                      ]}
                    />
                  </div>
                </TabPane>

                <TabPane tabId="services">
                  <h4 className="mb-4">Services & Expertise</h4>
                  <Row>
                    <Col md={6}>
                      <ArrayInput
                        label="Business Categories"
                        values={formData.categories || []}
                        onChange={(values) =>
                          setFormData((prev) => ({
                            ...prev,
                            categories: values,
                          }))
                        }
                        placeholder="Enter a category"
                      />
                    </Col>
                    <Col md={6}>
                      <ArrayInput
                        label="Services Offered"
                        values={formData.services || []}
                        onChange={(values) =>
                          setFormData((prev) => ({
                            ...prev,
                            services: values,
                          }))
                        }
                        placeholder="Enter a service"
                      />
                    </Col>
                  </Row>

                  <Row className="g-4">
                    <Col md={6}>
                      <ArrayInput
                        label="Products"
                        values={formData.servicesProducts || []}
                        onChange={(values) =>
                          setFormData((prev) => ({
                            ...prev,
                            servicesProducts: values,
                          }))
                        }
                        placeholder="Enter a product"
                      />
                    </Col>
                    <Col md={6}>
                      <ArrayInput
                        label="Brands"
                        values={formData.brands || []}
                        onChange={(values) =>
                          setFormData((prev) => ({
                            ...prev,
                            brands: values,
                          }))
                        }
                        placeholder="Enter a brand"
                      />
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="credentials">
                  <Row className="g-4">
                    <Col md={6}>
                      <Card className="border-0 shadow-sm mb-4">
                        <CardBody className="p-4">
                          <h4 className="mb-2 d-flex align-items-center">
                            <Shield className="me-2" size={20} />
                            License Information
                          </h4>
                          <ObjectInput
                            label=""
                            value={formData.licenses?.[0] || {}}
                            onChange={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                licenses: [value],
                              }))
                            }
                            fields={[
                              {
                                key: "type",
                                label: "License Type",
                                type: "text",
                              },
                              {
                                key: "number",
                                label: "License Number",
                                type: "text",
                              },
                              {
                                key: "state",
                                label: "State",
                                type: "text",
                              },
                              {
                                key: "expiration",
                                label: "Expiration Date",
                                type: "date",
                              },
                            ]}
                          />
                        </CardBody>
                      </Card>

                      <Card className="border-0 shadow-sm mb-4">
                        <CardBody className="p-4">
                          <h4 className="mb-2 d-flex align-items-center">
                            <Award className="me-2" size={20} />
                            Accreditations
                          </h4>
                          <ArrayInput
                            label=""
                            values={formData.accreditation || []}
                            onChange={(values) =>
                              setFormData((prev) => ({
                                ...prev,
                                accreditation: values,
                              }))
                            }
                            placeholder="Enter an accreditation"
                          />
                        </CardBody>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card className="border-0 shadow-sm mb-4">
                        <CardBody className="p-4">
                          <h4 className="mb-2 d-flex align-items-center">
                            <FileText className="me-2" size={20} />
                            Insurance Details
                          </h4>
                          <ObjectInput
                            label=""
                            value={formData.insurance || {}}
                            onChange={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                insurance: value,
                              }))
                            }
                            fields={[
                              {
                                key: "active",
                                label: "Active",
                                type: "checkbox",
                              },
                              {
                                key: "type",
                                label: "Insurance Type",
                                type: "text",
                              },
                              {
                                key: "provider",
                                label: "Provider",
                                type: "text",
                              },
                              {
                                key: "coverageAmount",
                                label: "Coverage Amount",
                                type: "text",
                              },
                              {
                                key: "expirationDate",
                                label: "Expiration Date",
                                type: "date",
                              },
                            ]}
                          />
                        </CardBody>
                      </Card>

                      <Card className="border-0 shadow-sm mb-4">
                        <CardBody className="p-4">
                          <h4 className="mb-2 d-flex align-items-center">
                            <Shield className="me-2" size={20} />
                            Bond Information
                          </h4>
                          <ObjectInput
                            label=""
                            value={formData.bond || {}}
                            onChange={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                bond: value,
                              }))
                            }
                            fields={[
                              {
                                key: "active",
                                label: "Active",
                                type: "checkbox",
                              },
                              {
                                key: "type",
                                label: "Bond Type",
                                type: "text",
                              },
                              {
                                key: "amount",
                                label: "Bond Amount",
                                type: "text",
                              },
                              {
                                key: "issuer",
                                label: "Issuer",
                                type: "text",
                              },
                              {
                                key: "expirationDate",
                                label: "Expiration Date",
                                type: "date",
                              },
                            ]}
                          />
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="team">
                  <Row className="g-4">
                    <Col md={6}>
                      <Card className="border-0 shadow-sm mb-0">
                        <CardBody className="p-4">
                          <h4 className="mb-4 d-flex align-items-center">
                            <Users className="me-2" size={20} />
                            Team Members
                          </h4>
                          {formData.employees?.map((employee, index) => (
                            <div
                              key={index}
                              className="mb-4 pb-2 border-bottom"
                            >
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Employee {index + 1}</h5>
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  onClick={() => {
                                    const newEmployees = [
                                      ...(formData.employees || []),
                                    ];
                                    newEmployees.splice(index, 1);
                                    setFormData((prev) => ({
                                      ...prev,
                                      employees: newEmployees,
                                    }));
                                  }}
                                >
                                  Remove
                                </Button>
                              </div>
                              <ObjectInput
                                label=""
                                value={employee}
                                onChange={(value) => {
                                  const newEmployees = [
                                    ...(formData.employees || []),
                                  ];
                                  newEmployees[index] = value;
                                  setFormData((prev) => ({
                                    ...prev,
                                    employees: newEmployees,
                                  }));
                                }}
                                fields={[
                                  {
                                    key: "name",
                                    label: "Name",
                                    type: "text",
                                  },
                                  {
                                    key: "title",
                                    label: "Title",
                                    type: "text",
                                  },
                                  {
                                    key: "experience",
                                    label: "Years of Experience",
                                    type: "number",
                                  },
                                  {
                                    key: "certifications",
                                    label: "Certifications",
                                    type: "textarea",
                                  },
                                ]}
                              />
                              <div className="mt-3">
                                <ImageUpload
                                  label="Employee Photo"
                                  currentImage={employee.avatar}
                                  onUpload={(url) => {
                                    const newEmployees = [
                                      ...(formData.employees || []),
                                    ];
                                    newEmployees[index] = {
                                      ...employee,
                                      avatar: url,
                                    };
                                    setFormData((prev) => ({
                                      ...prev,
                                      employees: newEmployees,
                                    }));
                                  }}
                                  folder="employee-photos"
                                />
                              </div>
                            </div>
                          ))}
                          <Button
                            color="primary"
                            outline
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                employees: [
                                  ...(prev.employees || []),
                                  { name: "", title: "" },
                                ],
                              }));
                            }}
                          >
                            Add Team Member
                          </Button>
                        </CardBody>
                      </Card>
                    </Col>

                    <Col md={6}>
                      <Card className="border-0 shadow-sm mb-0">
                        <CardBody className="p-4">
                          <h4 className="mb-2 d-flex align-items-center">
                            <Clock className="me-2" size={20} />
                            Business Hours
                          </h4>
                          <TimeInput
                            label=""
                            value={formData.hours || {}}
                            onChange={(value) =>
                              setFormData((prev) => ({ ...prev, hours: value }))
                            }
                          />
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </TabPane>
              </TabContent>

              <div className="mt-4 d-flex justify-content-between align-items-center">
                <Button
                  type="button"
                  color="secondary"
                  className="px-4"
                  onClick={() => navigate("/contractors")}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <div className="d-flex gap-2">
                  <Button
                    type="button"
                    color="light"
                    onClick={() => {
                      const prevTab = {
                        basic: "team",
                        contact: "basic",
                        services: "contact",
                        credentials: "services",
                        team: "credentials",
                      }[activeTab];
                      setActiveTab(prevTab as string);
                    }}
                    disabled={activeTab === "basic"}
                  >
                    Previous
                  </Button>
                  {activeTab !== "team" ? (
                    <Button
                      type="button"
                      color="primary"
                      onClick={(e) => {
                        e.preventDefault();
                        const nextTab = {
                          basic: "contact",
                          contact: "services",
                          services: "credentials",
                          credentials: "team",
                        }[activeTab];
                        setActiveTab(nextTab as string);
                      }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button type="submit" color="success" disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : isEditMode ? (
                        "Update Contractor"
                      ) : (
                        "Add Contractor"
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ContractorFormPage;
